import {
  FlowchartNodeShape,
  type FlowchartNode,
  type FlowchartSubgraph,
} from "./parse-mermaid-flowchart";
import {
  GROUP_NODE_MIN_HEIGHT,
  GROUP_NODE_MIN_WIDTH,
  GROUP_NODE_PADDING_X,
  NODE_BASE_HEIGHT,
  NODE_HORIZONTAL_PADDING,
  NODE_MAX_WIDTH,
  NODE_MIN_WIDTH,
  NODE_TEXT_CHAR_WIDTH,
  SHAPE_SIZE_ADJUSTMENT,
} from "./flowchart-constants";
import type { NodeSize } from "./flowchart-layout-types";

const NODE_EXTRA_LINE_HEIGHT = 18;

export function getNodeLabel(node: FlowchartNode): string {
  return node.label && node.label.length > 0 ? node.label : node.id;
}

export function getNodeShape(node: FlowchartNode): FlowchartNodeShape {
  return node.shape ?? FlowchartNodeShape.Process;
}

function getLineWidth(line: string): number {
  return Math.max(line.length, 1) * NODE_TEXT_CHAR_WIDTH;
}

function getWrappedLineCount(input: {
  line: string;
  maxTextWidth: number;
}): number {
  return Math.max(
    1,
    Math.ceil(getLineWidth(input.line) / Math.max(input.maxTextWidth, 1)),
  );
}

function getTextLineCount(input: {
  lines: readonly string[];
  maxTextWidth: number;
}): number {
  return input.lines.reduce((total, line) => {
    return (
      total + getWrappedLineCount({ line, maxTextWidth: input.maxTextWidth })
    );
  }, 0);
}

export function getTextSize(label: string): NodeSize {
  const maxTextWidth = NODE_MAX_WIDTH - NODE_HORIZONTAL_PADDING * 2;
  const lines = label.split("\n");
  const longestLineLength = lines.reduce(
    (longest, line) => Math.max(longest, line.length),
    1,
  );
  const textWidth = longestLineLength * NODE_TEXT_CHAR_WIDTH;
  const width = Math.max(
    NODE_MIN_WIDTH,
    Math.min(NODE_MAX_WIDTH, textWidth + NODE_HORIZONTAL_PADDING * 2),
  );
  const lineCount = getTextLineCount({ lines, maxTextWidth });
  const height =
    NODE_BASE_HEIGHT + (Math.max(1, lineCount) - 1) * NODE_EXTRA_LINE_HEIGHT;
  return { width, height };
}

export function getNodeSize(node: FlowchartNode): NodeSize {
  const shape = getNodeShape(node);
  const baseSize = getTextSize(getNodeLabel(node));
  const adjustment = SHAPE_SIZE_ADJUSTMENT[shape];
  const width = Math.max(
    adjustment.minWidth,
    baseSize.width + adjustment.extraWidth,
  );
  const height = Math.max(
    adjustment.minHeight,
    baseSize.height + adjustment.extraHeight,
  );
  if (!adjustment.square) {
    return { width, height };
  }

  const squareSize = Math.max(width, height);
  const maxSquareSize = adjustment.maxSquareSize ?? squareSize;
  const clampedSquareSize = Math.min(squareSize, maxSquareSize);
  return { width: clampedSquareSize, height: clampedSquareSize };
}

export function getSubgraphDepth(input: {
  subgraph: FlowchartSubgraph;
  subgraphById: ReadonlyMap<string, FlowchartSubgraph>;
}): number {
  if (!input.subgraph.parent) {
    return 0;
  }

  const parent = input.subgraphById.get(input.subgraph.parent);
  if (!parent) {
    return 0;
  }

  return (
    getSubgraphDepth({ subgraph: parent, subgraphById: input.subgraphById }) + 1
  );
}

export function getSubgraphSeedSize(subgraph: FlowchartSubgraph): NodeSize {
  const label = subgraph.label ?? subgraph.id;
  const labelWidth = Math.max(label.length, 1) * NODE_TEXT_CHAR_WIDTH;
  return {
    width: Math.max(
      GROUP_NODE_MIN_WIDTH,
      labelWidth + GROUP_NODE_PADDING_X * 2,
    ),
    height: GROUP_NODE_MIN_HEIGHT,
  };
}
